import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { loginUser } from '../api/services';
import { AuthContext } from '../context/auth-context';
import { Box, TextField, Button, Typography, IconButton, InputAdornment, CircularProgress, Container, Grid } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginImage from '../assets/Login-Image.png';
import Navbar from '../components/NavBar';
import Footer from '../components/Footer';

const LoginPage = () => {
  const { login, isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user?.role) {
      const redirectTo = location.state?.from || 
        (user.role === 'MANAGER' ? '/manager-dashboard' : 
         ['ADMIN', 'FINANCIAL_ADMIN'].includes(user.role) ? '/dashboard' : '/home');
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, location, user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const trimmedEmail = email.trim();
      const trimmedPassword = password.trim();

      if (!trimmedEmail || !trimmedPassword) {
        setError('Email and password are required.');
        setIsLoading(false);
        return;
      }

      const credentials = { email: trimmedEmail, password: trimmedPassword };
      const response = await loginUser(credentials);
      const { data, valid } = response;
      
      if (!valid || !data) {
        throw new Error('Invalid response from server.');
      }

      const { token, name, email: userEmail, role, department, employeeId, jobTitle } = data;
      let normalizedRole = role ? role.toUpperCase() : 'EMPLOYEE';
      if (!['EMPLOYEE', 'CONSULTANT', 'ADMIN', 'FINANCIAL_ADMIN', 'INTERN', 'MANAGER'].includes(normalizedRole)) {
        normalizedRole = 'EMPLOYEE';
      }
      login(token, {
        name,
        email: userEmail,
        role: normalizedRole,
        department,
        employeeId,
        jobTitle,
      });

      const redirectTo = location.state?.from || 
        (normalizedRole === 'MANAGER' ? '/manager-dashboard' : 
         ['ADMIN', 'FINANCIAL_ADMIN'].includes(normalizedRole) ? '/dashboard' : '/home');
      navigate(redirectTo, { replace: true });
    } catch (err) {
      let errorMessage = 'An unexpected error occurred. Please try again.';
      if (err.response) {
        errorMessage = err.response.data?.message || `Login failed with status ${err.response.status}. Please try again.`;
      } else if (err.request) {
        errorMessage = 'No response from the server. Please check your network connection.';
      } else {
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <>
      <Navbar />
      <Box sx={{ 
        minHeight: 'calc(100vh - 140px)',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3 }
      }}>
        <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid container sx={{ 
            minHeight: '500px',
            boxShadow: { xs: 'none', sm: '0 4px 20px rgba(0, 0, 0, 0.1)' },
            borderRadius: { xs: 0, sm: '12px' },
            overflow: 'hidden',
            border: { xs: 'none', sm: '1px solid #e0e0e0' }
          }}>
            {/* Left Column - Login Form */}
            <Grid item xs={12} md={6} sx={{ 
              p: { xs: 3, sm: 4, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              backgroundColor: '#fff'
            }}>
              <Box sx={{ 
                width: '100%',
                maxWidth: { xs: '100%', sm: '400px' },
                mx: 'auto'
              }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    fontSize: { xs: '1.5rem', sm: '2rem' }, 
                    color: '#000', 
                    mb: 1,
                    textAlign: { xs: 'center', sm: 'left' }
                  }}
                >
                  Welcome back
                </Typography>
                
                <Typography 
                  sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' }, 
                    color: '#666', 
                    mb: 3,
                    textAlign: { xs: 'center', sm: 'left' }
                  }}
                >
                  Please enter your details
                </Typography>
                
                <form onSubmit={handleLogin}>
                  {error && (
                    <Typography 
                      color="error" 
                      sx={{ 
                        mb: 2, 
                        textAlign: 'center',
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        p: 1,
                        backgroundColor: '#ffebee',
                        borderRadius: '4px'
                      }}
                    >
                      {error}
                    </Typography>
                  )}
                  
                  <TextField
                    label="Email address"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    type="email"
                    fullWidth
                    sx={{ 
                      mb: 2, 
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '8px',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      },
                      '& .MuiInputLabel-root': { 
                        color: '#666',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }
                    }}
                    size="small"
                  />
                  
                  <TextField
                    label="Password"
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    sx={{ 
                      mb: 2,
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: '8px',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      },
                      '& .MuiInputLabel-root': { 
                        color: '#666',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton 
                            onClick={handleTogglePasswordVisibility} 
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    mb: 3 
                  }}>
                    <RouterLink to="/forgot-password" style={{ textDecoration: 'none' }}>
                      <Typography sx={{ 
                        color: '#009db9', 
                        fontSize: { xs: '0.85rem', sm: '0.9rem' }, 
                        '&:hover': { textDecoration: 'underline' } 
                      }}>
                        Forgot password?
                      </Typography>
                    </RouterLink>
                  </Box>
                  
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    sx={{
                      py: { xs: 1.25, sm: 1.5 },
                      borderRadius: '8px',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                      fontWeight: 'bold',
                      backgroundColor: '#009db9',
                      color: '#fff',
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#008ba3' },
                      '&:disabled': { backgroundColor: '#b2ebf2' }
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Login'
                    )}
                  </Button>
                  
                  {/* Mobile-only alternative for small screens */}
                  <Box sx={{ 
                    display: { xs: 'block', sm: 'none' }, 
                    mt: 3,
                    textAlign: 'center' 
                  }}>
                  </Box>
                </form>
              </Box>
            </Grid>
            
            {/* Right Column - Image */}
            <Grid 
              item 
              xs={12} 
              md={6} 
              sx={{ 
                display: { xs: 'none', md: 'flex' },
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#f5f9fa'
              }}
            >
              <Box
                component="img"
                src={LoginImage}
                alt="Welcome Background"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
              
              {/* Mobile Image - Show only on small screens */}
              <Box
                component="img"
                src={LoginImage}
                alt="Welcome Background"
                sx={{
                  display: { xs: 'block', md: 'none' },
                  width: '100%',
                  height: '200px',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  mt: 3
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default LoginPage;